/**
 * Applies statistics and both advertising choices to HubSpot.
 * Owns its browser loader and consent APIs; privacyTracking coordinates account
 * changes and the refresh needed to remove an already running SDK.
 */
import { getReviewedTrackingPage } from "@/utils/trackingLocation"

type HubSpotCommand = [string, ...unknown[]]
type HubSpotWindow = typeof window & {
	_hsq?: HubSpotCommand[]
	_hsp?: HubSpotCommand[]
	disableHubSpotCookieBanner?: boolean
}

enum HubSpotState {
	Idle,
	Loading,
	Active,
	Stopped,
}

const portalId = import.meta.env.VITE_HUBSPOT_PORTAL_ID ?? "8138613"
const browser = window as HubSpotWindow
const trackingCookies = ["hubspotutk", "__hstc", "__hssc", "__hssrc"]
const consentDenied = { analytics: false, advertisement: false, functionality: false }
let currentConsent = { ...consentDenied }
let state = HubSpotState.Idle
let lastSentPath: string | undefined
let script: HTMLScriptElement | undefined
let consentRevocationPending = false

function revokeConsentWhenAllowed(allowVendorReload: boolean): void {
	if (!allowVendorReload || !consentRevocationPending) return
	consentRevocationPending = false
	sendConsent(false)
	browser._hsp?.push(["revokeCookieConsent"])
	// Revocation can remove the do-not-track cookie.
	browser._hsq?.push(["doNotTrack"])
}

function sendConsent(analytics: boolean, advertisement = false): void {
	currentConsent = { ...consentDenied, analytics, advertisement }
	browser._hsp?.push(["setHubSpotConsent", { ...currentConsent }])
}

function clearTrackingCookies(): void {
	// HubSpot shares these cookies with the public website. Clear all visible
	// scopes so a new account cannot inherit the previous visitor's identity.
	try {
		for (const name of trackingCookies) {
			for (const domain of ["", "; Domain=app.landerz.ca", "; Domain=landerz.ca"]) {
				document.cookie = `${name}=; Max-Age=0; Path=/${domain}; SameSite=Lax; Secure`
			}
		}
	} catch (error) {
		// Cookie restrictions must not prevent the remaining tools from stopping
		// or the coordinator from refreshing the page.
		console.warn("Could not clear HubSpot visitor cookies", error)
	}
}

function disableTracking(): void {
	// revokeCookieConsent can remove the do-not-track cookie. Keep the SDK's
	// runtime switch off too, and apply doNotTrack after cookie revocation.
	browser._hsq?.push(["setTrackingEnabled", false])
	browser._hsq?.push(["doNotTrack"])
}

export function applyHubSpotTrackingChoices(
	allowStatistics: boolean,
	allowMeasurement: boolean,
	allowPersonalization = false
): boolean {
	// HubSpot's installed pixels combine measurement and personalized advertising.
	// Neither choice alone permits that collection.
	const advertisingAllowed = allowMeasurement && allowPersonalization
	if (!allowStatistics && !advertisingAllowed) {
		if (state !== HubSpotState.Idle) stopHubSpotTracking()
		return false
	}
	if (state === HubSpotState.Stopped) return false
	if ((currentConsent.analytics && !allowStatistics) || (currentConsent.advertisement && !advertisingAllowed)) {
		stopHubSpotTracking()
		return false
	}
	if (
		import.meta.env.VITE_ENABLE_HUBSPOT_TRACKING === "false" ||
		window.location.hostname !== "app.landerz.ca" ||
		!/^\d+$/.test(portalId)
	) {
		return false
	}
	const page = getReviewedTrackingPage()
	if (!page) {
		if (state !== HubSpotState.Idle) stopHubSpotTracking()
		return false
	}
	const statisticsGranted = allowStatistics && !currentConsent.analytics
	if (
		state !== HubSpotState.Idle &&
		(currentConsent.analytics !== allowStatistics || currentConsent.advertisement !== advertisingAllowed)
	) {
		sendConsent(allowStatistics, advertisingAllowed)
		if (statisticsGranted) {
			browser._hsq?.push(["setTrackingEnabled", true])
			browser._hsq?.push(["doNotTrack", { track: true }])
		}
	}
	if (state === HubSpotState.Loading) {
		// Only keep the latest path while the analytics child script downloads.
		// The standard loader's load event does not mean that child is ready.
		browser._hsq?.push(["setPath", page.pathname])
		lastSentPath = page.pathname
		return true
	}
	if (state === HubSpotState.Active) {
		if (allowStatistics && (statisticsGranted || page.pathname !== lastSentPath)) {
			browser._hsq?.push(["setPath", page.pathname])
			browser._hsq?.push(["trackPageView"])
			lastSentPath = page.pathname
		}
		return true
	}

	state = HubSpotState.Loading
	lastSentPath = page.pathname
	browser.disableHubSpotCookieBanner = true
	browser._hsp = []
	browser._hsq = []
	// HubSpot's consent module also supplies the visitor cookies. Keep it, but
	// let our own banner own the choices. Functionality remains denied.
	browser._hsp.push([
		"addPrivacyConsentListener",
		() => {
			if (state === HubSpotState.Stopped) disableTracking()
		},
	])
	sendConsent(allowStatistics, advertisingAllowed)
	// setPath controls the report's page name, not every SDK field. HubSpot also
	// reads the full URL, title and referrer; see docs/privacy-choices.md.
	browser._hsq.push(["setPath", page.pathname])
	browser._hsq.push(["setTrackingEnabled", allowStatistics])
	browser._hsq.push(["doNotTrack", { track: allowStatistics }])
	// The SDK processes this listener after its own initial page view. Later
	// navigation can then use manual page views without duplicating that first one.
	browser._hsq.push([
		"addUserTokenListener",
		() => {
			if (state === HubSpotState.Loading) state = HubSpotState.Active
		},
	])

	script = document.createElement("script")
	script.id = "hs-script-loader"
	script.async = true
	script.defer = true
	script.referrerPolicy = "no-referrer"
	script.src = `https://js.hs-scripts.com/${portalId}.js`
	script.onerror = () => stopHubSpotTracking()
	document.head.appendChild(script)
	return true
}

export function stopHubSpotTracking(allowVendorReload = true): void {
	if (state === HubSpotState.Idle) return
	if (state === HubSpotState.Stopped) {
		revokeConsentWhenAllowed(allowVendorReload)
		return
	}
	state = HubSpotState.Stopped
	consentRevocationPending = true
	// Before initialization these are ordinary queues; after initialization their
	// push methods dispatch immediately. Discard pending grants in both cases.
	if (browser._hsq) browser._hsq.length = 0
	if (browser._hsp) browser._hsp.length = 0
	// Disable sends before changing vendor consent: its listeners may run
	// synchronously, and revocation itself can otherwise send a tracking event.
	browser._hsq?.push(["setTrackingEnabled", false])
	// HubSpot reloads immediately when its LinkedIn pixel receives denied consent.
	// Defer this vendor notification until a reload can no longer interrupt
	// sign-out cleanup. The analytics stop commands still run immediately.
	if (allowVendorReload) revokeConsentWhenAllowed(true)
	else browser._hsq?.push(["doNotTrack"])
	clearTrackingCookies()
	if (script) {
		script.onerror = null
		script.remove()
	}
	// Removing the loader cannot undo an executed child script. The coordinator
	// refreshes the page after withdrawal; the stop commands protect that interval.
}
